import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';
import { ACTIVITY_TYPES, ACTIVITY_RESULTS, TASK_PRIORITIES, contactName } from './constants';

const EMPTY = {
  accountId: '', contactId: 'none', type: 'phone_call', reached: 'yes',
  discussed: '', result: '', nextStep: '', followUpDate: '', priority: 'medium',
};

export const LogConversationModal = ({ open, onOpenChange, onSaved, defaultAccountId, defaultContactId }) => {
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, accountId: defaultAccountId || '', contactId: defaultContactId || 'none' });
    crmFetch('/accounts').then((r) => setAccounts(r.data || []));
  }, [open, defaultAccountId, defaultContactId]);

  useEffect(() => {
    if (!open || !form.accountId) { setContacts([]); return; }
    crmFetch(`/contacts?accountId=${form.accountId}`).then((r) => setContacts(r.data || []));
  }, [open, form.accountId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.accountId) { toast.error('Please select an account'); return; }
    setSaving(true);
    try {
      const res = await crmFetch('/activities', {
        method: 'POST',
        body: JSON.stringify({
          accountId: form.accountId,
          contactId: form.contactId === 'none' ? null : form.contactId,
          type: form.type,
          reached: form.reached === 'yes',
          discussed: form.discussed,
          result: form.result || null,
          nextStep: form.nextStep,
          followUpDate: form.followUpDate || null,
          priority: form.priority,
        }),
      });
      if (!res.success) { toast.error(res.message || 'Failed to log conversation'); return; }
      toast.success(res.data?.task ? 'Conversation logged · Follow-up task created' : 'Conversation logged');
      onOpenChange(false);
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="log-conversation-modal">
        <DialogHeader>
          <DialogTitle>Log Conversation</DialogTitle>
          <DialogDescription>Record a customer touchpoint. Setting a follow-up date auto-creates a task.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Account *</Label>
              <Select value={form.accountId} onValueChange={(v) => set('accountId', v) || set('contactId', 'none')}>
                <SelectTrigger data-testid="logconv-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Contact</Label>
              <Select value={form.contactId} onValueChange={(v) => set('contactId', v)} disabled={!form.accountId}>
                <SelectTrigger data-testid="logconv-contact-select"><SelectValue placeholder="Select contact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific contact</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{contactName(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Type</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger data-testid="logconv-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Reached?</Label>
              <Select value={form.reached} onValueChange={(v) => set('reached', v)}>
                <SelectTrigger data-testid="logconv-reached-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block">What was discussed?</Label>
            <Textarea rows={3} value={form.discussed} onChange={(e) => set('discussed', e.target.value)}
              placeholder="Key points from the conversation…" data-testid="logconv-discussed-input" />
          </div>

          <div>
            <Label className="mb-1.5 block">Result</Label>
            <Select value={form.result} onValueChange={(v) => set('result', v)}>
              <SelectTrigger data-testid="logconv-result-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
              <SelectContent>
                {ACTIVITY_RESULTS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1.5 block">Next Step</Label>
            <Textarea rows={2} value={form.nextStep} onChange={(e) => set('nextStep', e.target.value)}
              placeholder="e.g. Get feedback on Enterprise Architect" data-testid="logconv-nextstep-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Follow Up Date</Label>
              <Input type="date" value={form.followUpDate} onChange={(e) => set('followUpDate', e.target.value)} data-testid="logconv-followup-date" />
            </div>
            {form.followUpDate && (
              <div>
                <Label className="mb-1.5 block">Task Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                  <SelectTrigger data-testid="logconv-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="logconv-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="logconv-save-btn">
              {saving ? 'Saving…' : (form.followUpDate ? 'Save & Create Task' : 'Save Conversation')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
